import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ResponsiveLine } from '@nivo/line';

import { useUser } from '../../../contexts/UserContext';
import {
  dmarcApi,
  fmtEpoch,
  fmtNumber,
  fmtPct,
  DomainSummary,
  TimeseriesPoint,
  SourceIp,
  ReportListItem,
} from '../../../services/dmarcApi';
import SourceKindChip from './SourceKindChip';

const DomainDetail: React.FC = () => {
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const { domain: domainParam } = useParams<{ domain: string }>();
  const domain = domainParam ? decodeURIComponent(domainParam) : '';

  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<DomainSummary | null>(null);
  const [series, setSeries] = useState<TimeseriesPoint[] | null>(null);
  const [topIps, setTopIps] = useState<SourceIp[] | null>(null);
  const [reports, setReports] = useState<ReportListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin()) navigate('/app/welcome');
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin() || !domain) return;
    setSummary(null);
    setSeries(null);
    setTopIps(null);
    dmarcApi
      .getDomain(domain, days)
      .then(res => {
        setSummary(res.summary);
        setSeries(res.timeseries);
        setTopIps(res.top_source_ips);
      })
      .catch(e => setError(e instanceof Error ? e.message : 'load failed'));
  }, [domain, days, isAdmin]);

  useEffect(() => {
    if (!isAdmin() || !domain) return;
    dmarcApi
      .listReports(domain, 50)
      .then(r => setReports(r.reports))
      .catch(() => {});
  }, [domain, isAdmin]);

  if (!isAdmin()) return null;

  const passRateData = series && series.length > 0
    ? [
        {
          id: 'pass_rate',
          data: series.map(p => ({
            x: p.date,
            y: p.pass_rate == null ? null : p.pass_rate * 100,
          })),
        },
      ]
    : [];

  const volumeData = series && series.length > 0
    ? [
        {
          id: 'messages',
          data: series.map(p => ({ x: p.date, y: p.total_messages })),
        },
      ]
    : [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        component={RouterLink}
        to="/app/admin/dmarc"
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 2 }}
      >
        Dashboard
      </Button>
      <Typography variant="h4" component="h1" fontWeight={600}>
        {domain}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        DMARC alignment detail
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load: {error}</Alert>}

      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Pass rate (30d)', value: summary ? fmtPct(summary.pass_rate_30d) : null },
          { label: 'Messages (30d)', value: summary ? fmtNumber(summary.messages_30d) : null },
          { label: 'Reports (30d)', value: summary ? fmtNumber(summary.report_count_30d) : null },
          {
            label: 'Last report',
            value: summary
              ? summary.last_seen_iso
                ? fmtEpoch(summary.last_seen_epoch)
                : 'No reports yet'
              : null,
          },
        ].map(stat => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={stat.label}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  {stat.label}
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ fontFeatureSettings: '"tnum"' }}>
                  {stat.value === null ? <CircularProgress size={20} /> : stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Typography variant="body2" color="text.secondary">
          Window:
        </Typography>
        <ButtonGroup size="small">
          {[7, 30, 90].map(d => (
            <Button
              key={d}
              variant={days === d ? 'contained' : 'outlined'}
              onClick={() => setDays(d)}
            >
              {d}d
            </Button>
          ))}
        </ButtonGroup>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          mb: 2,
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>
              Pass rate
            </Typography>
            <Box sx={{ width: '100%', height: 260, position: 'relative' }}>
              {!series && <CircularProgress size={24} />}
              {series && passRateData.length === 0 && (
                <Typography color="text.secondary" textAlign="center" py={8}>
                  No data in this window
                </Typography>
              )}
              {passRateData.length > 0 && (
                <ResponsiveLine
                  data={passRateData}
                  margin={{ top: 10, right: 20, bottom: 50, left: 50 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 0, max: 100 }}
                  axisLeft={{ format: v => `${v}%`, tickValues: 5 }}
                  axisBottom={{
                    tickRotation: -30,
                    format: v => String(v).slice(5),
                    tickValues: passRateData[0].data
                      .filter((_, i, a) => i % Math.max(1, Math.ceil(a.length / 7)) === 0)
                      .map(p => p.x),
                  }}
                  enableArea={false}
                  enablePoints={false}
                  useMesh
                  colors={['#1976d2']}
                  tooltip={({ point }) => (
                    <Paper sx={{ p: 1, fontSize: 12 }}>
                      {point.data.xFormatted}:{' '}
                      {typeof point.data.y === 'number' ? `${point.data.y.toFixed(1)}%` : '—'}
                    </Paper>
                  )}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>
              Message volume
            </Typography>
            <Box sx={{ width: '100%', height: 260, position: 'relative' }}>
              {!series && <CircularProgress size={24} />}
              {series && volumeData.length === 0 && (
                <Typography color="text.secondary" textAlign="center" py={8}>
                  No data in this window
                </Typography>
              )}
              {volumeData.length > 0 && (
                <ResponsiveLine
                  data={volumeData}
                  margin={{ top: 10, right: 20, bottom: 50, left: 60 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 0, max: 'auto' }}
                  axisLeft={{ format: v => Number(v).toLocaleString() }}
                  axisBottom={{
                    tickRotation: -30,
                    format: v => String(v).slice(5),
                    tickValues: volumeData[0].data
                      .filter((_, i, a) => i % Math.max(1, Math.ceil(a.length / 7)) === 0)
                      .map(p => p.x),
                  }}
                  enableArea
                  areaOpacity={0.2}
                  enablePoints={false}
                  useMesh
                  colors={['#1976d2']}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Top source IPs
          </Typography>
          {!topIps && <CircularProgress size={24} />}
          {topIps && topIps.length === 0 && (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No IPs seen in this window
            </Typography>
          )}
          {topIps && topIps.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Source IP</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell align="right">Messages</TableCell>
                    <TableCell align="right">Pass</TableCell>
                    <TableCell align="right">Fail</TableCell>
                    <TableCell align="right">Pass rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topIps.map(row => (
                    <TableRow hover key={row.source_ip}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {row.source_ip}
                      </TableCell>
                      <TableCell>
                        <SourceKindChip kind={row.source_kind} />
                      </TableCell>
                      <TableCell align="right">{fmtNumber(row.messages)}</TableCell>
                      <TableCell align="right">{fmtNumber(row.pass_dmarc)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          component="span"
                          sx={{ color: row.fail > 0 ? 'error.main' : 'text.primary' }}
                        >
                          {fmtNumber(row.fail)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum"' }}>
                        {fmtPct(row.pass_rate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Recent reports
          </Typography>
          {!reports && <CircularProgress size={24} />}
          {reports && reports.length === 0 && (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No reports received yet for this domain.
            </Typography>
          )}
          {reports && reports.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Reporter</TableCell>
                    <TableCell>Window</TableCell>
                    <TableCell align="right">Messages</TableCell>
                    <TableCell align="right">Pass</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map(r => (
                    <TableRow hover key={r.report_uid}>
                      <TableCell>{r.reporting_org}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {fmtEpoch(r.date_begin)} → {fmtEpoch(r.date_end)}
                      </TableCell>
                      <TableCell align="right">{fmtNumber(r.total_messages)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          variant="outlined"
                          color={r.pass_dmarc < r.total_messages ? 'warning' : 'default'}
                          label={`${fmtNumber(r.pass_dmarc)} / ${fmtNumber(r.total_messages)}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/app/admin/dmarc/domains/${encodeURIComponent(domain)}/reports/${encodeURIComponent(r.report_uid)}`}
                          underline="hover"
                        >
                          View
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default DomainDetail;
